import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Plus, Trash2, CheckCircle2, XCircle, Receipt, FileText, Filter } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const CATEGORIES = [
  { value: "labor",          label: "Labor" },
  { value: "material",       label: "Material" },
  { value: "equipment",      label: "Equipment" },
  { value: "travel",         label: "Travel" },
  { value: "permits",        label: "Permits & Fees" },
  { value: "subcontractor",  label: "Subcontractor" },
  { value: "fuel",           label: "Fuel" },
  { value: "tools",          label: "Tools" },
  { value: "other",          label: "Other" },
] as const;

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pending:     { label: "Pending",     className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  approved:    { label: "Approved",    className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  rejected:    { label: "Rejected",    className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  reimbursed:  { label: "Reimbursed", className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
};

const EMPTY_FORM = {
  description: "",
  amount: "",
  expense_date: new Date().toISOString().split("T")[0],
  category: "other" as string,
  vendor_name: "",
  notes: "",
};

interface Props {
  projectId: string;
}

export const ProjectExpensesTab = ({ projectId }: Props) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { can, isAdmin } = usePermissions();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const canManage = isAdmin || can("edit_work_orders") || can("approve_bills");
  const canAdd    = canManage || can("edit_work_orders") || can("submit_expenses");

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: expenses, isLoading } = useQuery({
    queryKey: ["project_expenses", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_expenses")
        .select("*")
        .eq("project_id", projectId)
        .order("expense_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const filtered = useMemo(() => {
    let rows = expenses ?? [];
    if (categoryFilter !== "all") rows = rows.filter((r: any) => r.category === categoryFilter);
    if (statusFilter !== "all") rows = rows.filter((r: any) => r.status === statusFilter);
    return rows;
  }, [expenses, categoryFilter, statusFilter]);

  const total = useMemo(() =>
    (expenses ?? []).reduce((s: number, r: any) => s + Number(r.amount), 0), [expenses]);

  const approvedTotal = useMemo(() =>
    (expenses ?? []).filter((r: any) => r.status === "approved" || r.status === "reimbursed")
      .reduce((s: number, r: any) => s + Number(r.amount), 0), [expenses]);

  const handleAdd = async () => {
    if (!form.description.trim()) return toast.error("Description is required");
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");

    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const { error } = await supabase.from("project_expenses").insert({
        company_id: companyId,
        project_id: projectId,
        description: form.description.trim(),
        amount,
        expense_date: form.expense_date,
        category: form.category,
        vendor_name: form.vendor_name.trim() || null,
        notes: form.notes.trim() || null,
        submitted_by: user?.id,
        status: "pending",
      });
      if (error) throw error;

      toast.success("Expense added");
      setForm({ ...EMPTY_FORM });
      setAddOpen(false);
      qc.invalidateQueries({ queryKey: ["project_expenses", projectId] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const { error } = await supabase.from("project_expenses").update({
        status: "approved",
        approved_by: user?.id,
        approved_at: new Date().toISOString(),
      }).eq("id", id);
      if (error) throw error;
      toast.success("Expense approved");
      qc.invalidateQueries({ queryKey: ["project_expenses", projectId] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleReject = async (id: string) => {
    try {
      const { error } = await supabase.from("project_expenses").update({ status: "rejected" }).eq("id", id);
      if (error) throw error;
      toast.success("Expense rejected");
      qc.invalidateQueries({ queryKey: ["project_expenses", projectId] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase.from("project_expenses").delete().eq("id", id);
      if (error) throw error;
      toast.success("Expense removed");
      qc.invalidateQueries({ queryKey: ["project_expenses", projectId] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Total Expenses</p>
          <p className="font-semibold text-lg">{fmt(total)}</p>
        </div>
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Approved / Reimbursed</p>
          <p className="font-semibold text-lg text-green-700 dark:text-green-400">{fmt(approvedTotal)}</p>
        </div>
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Pending</p>
          <p className="font-semibold text-lg text-amber-700 dark:text-amber-400">
            {fmt((expenses ?? []).filter((r: any) => r.status === "pending").reduce((s: number, r: any) => s + Number(r.amount), 0))}
          </p>
        </div>
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Count</p>
          <p className="font-semibold text-lg">{(expenses ?? []).length}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {canAdd && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Expense
          </Button>
        )}
        {filtered.length > 0 && <ExportToolbar entityType="expenses" data={filtered} filterDescription={`category: ${categoryFilter}, status: ${statusFilter}`} />}
        {isMobile ? (
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFiltersOpen(!filtersOpen)}>
            <Filter className="h-3.5 w-3.5 mr-1" /> Filters
            {(categoryFilter !== "all" || statusFilter !== "all") && <span className="ml-1 w-2 h-2 rounded-full bg-primary" />}
          </Button>
        ) : (
          <>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 h-8">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-8">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="reimbursed">Reimbursed</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} expense{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Mobile filter panel */}
      {isMobile && filtersOpen && (
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="flex-1 h-8">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 h-8">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="reimbursed">Reimbursed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No expenses recorded for this project.</p>
          {canAdd && (
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add First Expense
            </Button>
          )}
        </div>
      ) : isMobile ? (
        /* ── Mobile Card View ── */
        <div className="space-y-3">
          {filtered.map((row: any) => {
            const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
            const catLabel = CATEGORIES.find(c => c.value === row.category)?.label ?? row.category;
            const canDelete = canManage || (row.submitted_by === user?.id && row.status === "pending");
            return (
              <div key={row.id} className={cn("border rounded-lg p-3 space-y-2", row.status === "rejected" && "opacity-60")}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm truncate flex-1">{row.description}</span>
                  <Badge variant="outline" className={cn("text-[10px] ml-2 shrink-0", badge.className)}>{badge.label}</Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{format(new Date(row.expense_date), "MMM d, yyyy")}</span>
                  <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                  {row.vendor_name && <span>· {row.vendor_name}</span>}
                </div>
                <div className="text-lg font-bold">{fmt(Number(row.amount))}</div>
                {row.bill_id && (
                  <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary cursor-pointer" onClick={() => navigate(`/controller/bills?billId=${row.bill_id}`)}>
                    Auto • Bill
                  </Badge>
                )}
                {row.notes && !row.bill_id && <p className="text-xs text-muted-foreground line-clamp-2">{row.notes}</p>}
                {(canManage || canDelete) && (
                  <div className="flex items-center gap-2 pt-1">
                    {row.status === "pending" && canManage && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleApprove(row.id)}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleReject(row.id)}><XCircle className="h-4 w-4" /></Button>
                      </>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive ml-auto" onClick={() => handleDelete(row.id)} disabled={deletingId === row.id}>
                        {deletingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Desktop Table View ── */
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Vendor / Payee</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row: any) => {
                const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
                const catLabel = CATEGORIES.find(c => c.value === row.category)?.label ?? row.category;
                const canDelete = canManage || (row.submitted_by === user?.id && row.status === "pending");
                return (
                  <TableRow key={row.id} className={cn(row.status === "rejected" && "opacity-60")}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {format(new Date(row.expense_date), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-sm flex items-center gap-1">
                        {row.bill_id ? (
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 cursor-pointer group" onClick={() => navigate(`/controller/bills?billId=${row.bill_id}`)}>
                                  <FileText className="inline h-3.5 w-3.5 text-primary shrink-0 group-hover:text-primary/70 transition-colors" />
                                  <span className="group-hover:underline underline-offset-2">{row.description}</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">View source bill</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          row.description
                        )}
                      </div>
                      {row.bill_id && (
                        <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary mt-0.5 cursor-pointer hover:bg-primary/20 transition-colors" onClick={() => navigate(`/controller/bills?billId=${row.bill_id}`)}>
                          Auto • Bill
                        </Badge>
                      )}
                      {row.notes && !row.bill_id && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{row.notes}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{row.vendor_name ?? "—"}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(Number(row.amount))}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-[10px]", badge.className)}>{badge.label}</Badge>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {row.status === "pending" && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-700" onClick={() => handleApprove(row.id)} title="Approve"><CheckCircle2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => handleReject(row.id)} title="Reject"><XCircle className="h-4 w-4" /></Button>
                            </>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(row.id)} disabled={deletingId === row.id} title="Delete">
                              {deletingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Expense Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" /> Add Project Expense
            </DialogTitle>
            <DialogDescription>
              Record a direct project cost (labor, material, travel, permits, etc.)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Description *</label>
              <Input
                placeholder="e.g. Permit fee — City of Austin"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Amount *</label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Date *</label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Category</label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Vendor / Payee</label>
                <Input
                  placeholder="Who was paid"
                  value={form.vendor_name}
                  onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea
                placeholder="Additional details, reference number, etc."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={busy}>
              {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Adding…</> : "Add Expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
